import React, { useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Error, Label } from '@progress/kendo-react-labels';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from '../../../TypeGen/json-response';

interface PartLocationOption {
  LocationID: number;
  LocationName: string;
  QuantityOnHand: number;
}

interface AdjustInventoryDialogProps {
  partId: number;
  partNumber: string;
  unitOfMeasure: string;
  locations: PartLocationOption[];
  onClose: () => void;
  onSuccess: () => void;
}

const AdjustInventoryDialog: React.FC<AdjustInventoryDialogProps> = ({
  partId,
  partNumber,
  unitOfMeasure,
  locations,
  onClose,
  onSuccess
}) => {
  const [locationId, setLocationId] = useState<number | null>(locations[0]?.LocationID ?? null);
  const [newQuantity, setNewQuantity] = useState<number | null>(0);
  const [costPerUnit, setCostPerUnit] = useState<number | null>(0);
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedLocation = useMemo(
    () => locations.find((location) => location.LocationID === locationId) ?? null,
    [locations, locationId]
  );

  const resetAndClose = () => {
    setLocationId(locations[0]?.LocationID ?? null);
    setNewQuantity(0);
    setCostPerUnit(0);
    setNote('');
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!locationId) {
      setError('Please select a location.');
      return;
    }

    if (newQuantity === null || newQuantity < 0) {
      setError('New quantity cannot be negative.');
      return;
    }

    if (costPerUnit === null || costPerUnit < 0) {
      setError('Cost per unit cannot be negative.');
      return;
    }

    const currentQuantity = selectedLocation?.QuantityOnHand ?? 0;
    const quantityAdjustment = newQuantity - currentQuantity;

    if (quantityAdjustment > 0 && (!costPerUnit || costPerUnit <= 0)) {
      setError('Enter a cost per unit when increasing stock.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response: JsonResponse = await fetchApi('/api/ShopInventory/AdjustInventory', {
        PartID: partId,
        LocationID: locationId,
        NewQuantity: newQuantity,
        CostPerUnit: costPerUnit ?? 0,
        Note: note?.trim() ? note.trim() : null
      }, 'POST');

      if (response.Success === false) {
        setSubmitting(false);
        setError(response.ErrorMessage || 'Failed to adjust inventory.');
        return;
      }

      onSuccess();
      resetAndClose();
    } catch (err: any) {
      if (!err?.status) {
        alert('Failed to adjust inventory.');
      }
      setSubmitting(false);
      setError(err?.detail ?? 'Failed to adjust inventory.');
    }
  };

  const noLocationsAvailable = locations.length === 0;

  return (
    <Dialog
      title={`Adjust Inventory for ${partNumber}`}
      onClose={resetAndClose}
      className="dialog-w500"
    >
      <div className="k-form k-form-md mb-3">
        {error && <Error>{error}</Error>}

        <FieldWrapper>
          <Label>Location</Label>
          <DropDownList
            data={locations}
            textField="LocationName"
            dataItemKey="LocationID"
            value={selectedLocation}
            onChange={(e: DropDownListChangeEvent) => {
              const value = e.value as PartLocationOption | null;
              setLocationId(value?.LocationID ?? null);
            }}
            disabled={submitting || noLocationsAvailable}
            style={{ width: '100%' }}
          />
          {selectedLocation && (
            <div className="text-muted small mt-1">
              Current on hand: {selectedLocation.QuantityOnHand} {unitOfMeasure}
            </div>
          )}
          {noLocationsAvailable && (
            <div className="alert alert-warning mt-2" role="alert">
              This part does not have any storage locations yet. Add a location before adjusting inventory.
            </div>
          )}
        </FieldWrapper>

        <div className="row">
          <div className="col-md-6">
            <FieldWrapper>
              <Label>New Quantity</Label>
              <NumericTextBox
                value={newQuantity}
                onChange={(e) => setNewQuantity(e.value ?? null)}
                min={0}
                step={1}
                format="n0"
                disabled={submitting}
                style={{ width: '100%' }}
              />
              <div className="text-muted small mt-1">Enter the actual quantity on hand after recount.</div>
            </FieldWrapper>
          </div>
          <div className="col-md-6">
            <FieldWrapper>
              <Label>Cost Per {unitOfMeasure}</Label>
              <NumericTextBox
                value={costPerUnit}
                onChange={(e) => setCostPerUnit(e.value ?? null)}
                min={0}
                step={0.01}
                format="c2"
                disabled={submitting}
                style={{ width: '100%' }}
              />
              <div className="text-muted small mt-1">Required for increases, optional for decreases.</div>
            </FieldWrapper>
          </div>
        </div>

        <FieldWrapper>
          <Label>Notes</Label>
          <TextArea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.value)}
            disabled={submitting}
          />
        </FieldWrapper>
      </div>
      <DialogActionsBar>
        <Button
          type="button"
          onClick={resetAndClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          themeColor="primary"
          disabled={submitting || noLocationsAvailable}
          onClick={handleSave}
        >
          {submitting ? 'Saving...' : 'Adjust Inventory'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default AdjustInventoryDialog;
